import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { useAccount } from 'wagmi'

import { getCorrespondChain, tokensName, config } from '@/config'
import type { AirDropEvent } from '@/types/event-values'
import type { StoremanInfoType } from '@/types/storeman-type'
import type { TokenType } from '@/types/token-type'
import type { ReceiptResult } from '@/types/trans-params'
import { formatValue, fromWei, validGroupId } from '@/utils'
import { useHttp } from '@/utils/http'
import { callMetamaskFunc } from '@/utils/metamask'
import {
  useCallContractFunc,
  useConfig,
  useErc20Contract,
  useRecordAirDropEvent,
  useRecordAirDropFunc,
  useStoremanGroupDelegateFunc,
  useWanWeb3,
} from '@/utils/useWeb3'

import { useIwan } from './useWebSocket'

const WANSCAN_API =
  process.env.REACT_APP_WANSCAN_API || 'https://www.wanscan.org'

export const useQueryDepositAll = () => {
  const client = useCallContractFunc()
  return useQuery({
    queryKey: ['MultiCoinStakeDelegate', 'getDepositAll'],
    queryFn: async () => client('MultiCoinStakeDelegate', 'getDepositAll', {}),
  })
}

export const useQueryCurrentStoremanGroupID = () => {
  const client = useCallContractFunc()
  return useQuery({
    queryKey: ['MultiCoinStakeDelegate', 'getCurrentStoremanGroupID'],
    queryFn: async () =>
      client('MultiCoinStakeDelegate', 'getCurrentStoremanGroupID', {}),
  })
}

export const useQueryStoremanGroupStatus = (id: string) => {
  const client = useCallContractFunc()
  return useQuery({
    queryKey: ['MultiCoinStakeDelegate', `getStoremanGroupStatus_${id}`],
    queryFn: async () =>
      client('MultiCoinStakeDelegate', 'getStoremanGroupStatus', {
        params: [id],
      }),
    enabled: validGroupId(id),
  })
}

export const useQueryTokenPrice = () => {
  const client = useHttp()
  return useQuery({
    queryKey: ['FetchTokenPrice'],
    queryFn: async () =>
      client({
        params: {
          ids: tokensName.join(),
          vs_currencies: 'usd',
        },
      }),
  })
}

export const useQueryStakers = (symbol: TokenType) => {
  const { address, chainId } = useAccount()

  const client = useCallContractFunc()
  const tokenAddr = useMemo(
    () =>
      address && chainId && config[chainId]
        ? config[chainId].tokenAddrArr[symbol]
        : '',
    [address, chainId, symbol],
  )

  const { data: staker, isSuccess: isSuccessStaker } = useQuery({
    queryKey: ['MultiCoinStakeDelegate', `stakers_${symbol}`],
    queryFn: async () =>
      client('MultiCoinStakeDelegate', 'stakers', {
        params: [tokenAddr, address],
      }),
    enabled: !!address && !!tokenAddr,
  })

  const id = useMemo(() => staker && staker.quitGroupId, [staker])
  const { data: smgStatus, isSuccess: isSuccessSmgStatus } = useQuery({
    queryKey: ['MultiCoinStakeDelegate', `getStoremanGroupStatus_${id}`],
    queryFn: async () =>
      client('MultiCoinStakeDelegate', 'getStoremanGroupStatus', {
        params: [id],
      }),
    enabled: validGroupId(id),
  })

  return { staker, smgStatus, isSuccessStaker, isSuccessSmgStatus }
}

export const useQueryWanEarned = (tokenType: TokenType) => {
  const { address, chainId } = useAccount()
  const client = useRecordAirDropFunc()
  const tokenAddr = useMemo(
    () =>
      address &&
      chainId &&
      config[getCorrespondChain.get(chainId.toString()) || '']
        ? config[getCorrespondChain.get(chainId.toString()) || ''].tokenAddrArr[
            tokenType
          ]
        : '',
    [address, chainId, tokenType],
  )
  return useQuery({
    queryKey: ['RecordAirDrop', `userReward_${tokenType}`],
    queryFn: () => client('userReward', [tokenAddr, address || '']),
    enabled: !!address && !!tokenAddr,
  })
}

export const useQueryAirDropEvent = (tokenType: TokenType) => {
  const { address, chainId } = useAccount()

  const client = useRecordAirDropEvent()
  const wanWeb3 = useWanWeb3()
  const tokenAddr = useMemo(
    () =>
      chainId &&
      address &&
      config[getCorrespondChain.get(chainId.toString()) || '']
        ? config[getCorrespondChain.get(chainId.toString()) || ''].tokenAddrArr[
            tokenType
          ]
        : '',
    [address, chainId, tokenType],
  )
  const { data: airdropArr } = useQuery<AirDropEvent[]>({
    queryKey: ['RecordAirDrop', `AirDrop_${tokenType}`],
    queryFn: async () =>
      client('AirDrop', {
        filter: {
          token: tokenAddr.toLowerCase() || '',
          user: address || '',
        },
        fromBlock: 10000000,
        toBlock: 'latest',
      }),
    enabled: !!address && !!tokenAddr,
  })

  const blockInfoQueryProps = useMemo(
    () => ({
      queries: airdropArr
        ? airdropArr.map((eve: { blockNumber: number }) => {
            return {
              queryKey: ['user', eve.blockNumber],
              queryFn: async () => wanWeb3?.eth.getBlock(eve.blockNumber),
              enabled: !!airdropArr.length && !!wanWeb3,
            }
          })
        : [],
    }),
    [airdropArr, wanWeb3],
  )
  const blockInfo = useQueries(blockInfoQueryProps)

  return useMemo(() => {
    if (blockInfo.length && blockInfo.every((item) => !item.isLoading)) {
      const bInfo = {}
      blockInfo.forEach((item) => {
        if (typeof item.data === 'object') {
          // @ts-ignore
          bInfo[item.data.number] = item.data.timestamp
        }
      })
      airdropArr?.forEach(
        // @ts-ignore
        (item) => (item.returnValues.timestamp = bInfo[item.blockNumber]),
      )

      airdropArr?.forEach(
        (item) =>
          // @ts-ignore
          (item.returnValues.formatAmount = fromWei(item.returnValues.amount)),
      )
      return {
        airdropArr: airdropArr?.sort(
          // @ts-ignore
          (a, b) => b.returnValues.timestamp - a.returnValues.timestamp,
        ),
      }
    } else {
      return {}
    }
  }, [blockInfo, airdropArr])
}

export const useQueryWanDailyIncentive = (tokenType: TokenType) => {
  const { address, chainId } = useAccount()

  const client = useRecordAirDropFunc()
  const tokenAddr = useMemo(
    () =>
      chainId && address && config[chainId || '']
        ? config[chainId || ''].tokenAddrArr[tokenType]
        : '',
    [address, chainId, tokenType],
  )
  const { data, ...restData } = useQuery({
    queryKey: ['RecordAirDrop', `dailyIncentive_${tokenType}`],
    queryFn: () => client('dailyIncentive', [tokenAddr]),
    enabled: !!tokenAddr,
  })

  return {
    data: formatValue(data * 7, 'WAN'),
    ...restData,
  }
}

export const useQueryTokenAllowance = (tokenType: TokenType) => {
  const { address } = useAccount()
  const contractConfig = useConfig('MultiCoinStakeDelegate')

  const [contractInstance] = useErc20Contract(tokenType)
  return useQuery({
    queryKey: ['ERC20', `allowance_${tokenType}`],
    queryFn: () =>
      tokenType !== 'WAN'
        ? contractInstance.methods
            .allowance(address, contractConfig?.addr)
            .call()
        : Promise.resolve('0'),
    enabled: !!address && !!contractConfig,
  })
}

export const useQueryTransactionReceipt = () => {
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [txReceipt, setTxReceipt] = useState<object | string | undefined>(
    undefined,
  )
  const queryClient = useQueryClient()

  const fetchReceipt = useCallback(
    (txhash: string, cb: (params: ReceiptResult) => void) => {
      setIsLoading(true)
      const timer = setInterval(
        async () =>
          callMetamaskFunc('eth_getTransactionReceipt', (i: any) => i, [txhash])
            .then((ret) => {
              if (ret) {
                setTxReceipt(ret)
                setIsSuccess(true)
                setIsLoading(false)
                clearInterval(timer)
                queryClient.invalidateQueries()
                cb({ txReceipt: ret, isReceiptSuccess: true })
              }
            })
            .catch((error) => {
              console.log('useQueryTransactionReceipt', error)
              setTxReceipt('0x')
              setIsLoading(false)
              clearInterval(timer)
              cb({ txReceipt: '0x', isReceiptSuccess: false, error })
            }),
        2000,
      )
    },
    [queryClient],
  )

  return {
    txReceipt,
    isLoading,
    isSuccess,
    fetchReceipt,
  }
}

export const useSmgInfo = () => {
  const { data: smgId } = useQueryCurrentStoremanGroupID()
  const client = useStoremanGroupDelegateFunc()

  return useQuery({
    queryKey: ['StoremanGroupDelegate', 'getStoremanGroupInfo'],
    queryFn: () => client('getStoremanGroupInfo', [smgId]),
    enabled: !!smgId,
    staleTime: Infinity,
  })
}

export const useQueryPosStakeInfo = () => {
  const client = useHttp()
  return useQuery({
    queryKey: ['usePosStakeInfo'],
    queryFn: async () =>
      client({
        url: `${WANSCAN_API}/vlds_json`,
        strict: true,
      }),
  })
}

export const useQueryStoremanInfo = () => {
  const client = useHttp()
  return useQuery({
    queryKey: ['useStoremanInfo'],
    queryFn: async () =>
      client({
        url: `${WANSCAN_API}/storemanlist_json`,
        strict: true,
      }),
  })
}

export const useStoremanInfo = (): Array<StoremanInfoType> | [] => {
  const { data: storemanGroupList } = useIwan(['getStoremanGroupList'])

  const groupIdArr1 = useMemo(() => {
    return storemanGroupList
      ? storemanGroupList
          .filter((i: { status: string }) => Number(i.status) >= 4)
          .map((v: { groupId: string }) => v.groupId)
      : []
  }, [storemanGroupList])

  const groupIdArr2 = useMemo(() => {
    return storemanGroupList
      ? storemanGroupList
          .filter((i: { status: string }) => Number(i.status) < 4)
          .map((v: { groupId: string }) => v.groupId)
      : []
  }, [storemanGroupList])

  const { data: groupMember1 } = useIwan(
    ['getStoremanGroupMember'],
    { groupId: groupIdArr1 },
    !!groupIdArr1.length,
  )

  const { data: groupMember2 } = useIwan(
    ['getStoremanCandidates'],
    { groupId: groupIdArr2 },
    !!groupIdArr2.length,
  )

  const groupMemberArr = useMemo(() => {
    if (groupMember1 || groupMember2) {
      const temp = _.unionBy<StoremanInfoType>(
        (groupMember1 || []).concat(groupMember2 || []).flat(),
        'wkAddr',
      )
      return temp.filter((v) => {
        const ret = storemanGroupList.find((j: { groupId: string }) =>
          [v.groupId, v.nextGroupId].includes(j.groupId),
        )
        return !v.isWhite && !!ret
      })
    } else {
      return []
    }
  }, [groupMember1, groupMember2, storemanGroupList])

  const { data: storemanInfo } = useQueryStoremanInfo()

  return useMemo(() => {
    if (
      storemanInfo &&
      storemanInfo.length &&
      groupMemberArr &&
      groupMemberArr.length
    ) {
      const ret: any[] = []
      groupMemberArr.forEach((j) => {
        const tmp = storemanInfo.find(
          (v: StoremanInfoType) =>
            v.groupId === j.groupId &&
            v.wkAddr.toLowerCase() === j.wkAddr.toLowerCase(),
        )
        if (tmp) {
          j.activity = tmp.activity
          j.name = tmp.name
          j.imgdata = tmp.imgdata
          j.totalDeposit = tmp.totalDeposit
          j.totalDepositRaw = tmp.totalDepositRaw
          j.selfDeposit = tmp.selfDeposit
          j.delegateFee = tmp.delegateFee
          j.powerWeight = tmp.powerWeight
          j.status = tmp.status
          j.startTime = tmp.startTime
          j.endTime = tmp.endTime
          j.autoRenew = tmp.autoRenew
        } else {
          const groupInfo = storemanGroupList.find((i: any) =>
            [j.groupId, j.nextGroupId].includes(i.groupId),
          )
          j.activity = 'N/A'
          j.name = j.wkAddr
          j.delegateFee = groupInfo ? groupInfo.delegateFee : '0'
        }
        ret.push(j)
      })
      return ret
    } else {
      return []
    }
  }, [storemanInfo, groupMemberArr, storemanGroupList])
}

export const useQueryStatsInfo = () => {
  const client = useHttp()
  return useQuery({
    queryKey: ['useStatsInfo'],
    queryFn: async () =>
      client({
        url: `${WANSCAN_API}/apiInfo?type=stats`,
        strict: true,
      }),
  })
}

export const useQueryStoremanConf = () => {
  const { data: storemanConf } = useIwan(['getStoremanConf'])
  return useMemo(() => {
    return storemanConf
  }, [storemanConf])
}

export const useQueryVldAttr = (validatorAddress: string) => {
  const client = useHttp()
  return useQuery({
    queryKey: ['useQueryVldAttr', validatorAddress],
    queryFn: async () =>
      client({
        url: `${WANSCAN_API}/apiInfo?type=vld_attr&id=${validatorAddress.toLowerCase()}`,
        strict: true,
      }),
    enabled: !!validatorAddress,
  })
}
